import { useTranslation } from 'react-i18next'
import { Crown, User } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { LiveAgent, AgentStatus } from '@/types/liveSnapshot'

interface Props {
  agents: LiveAgent[]
  darkMode: boolean
}

// Mapping statut → dot couleur + libellé i18n
const STATUS_TONE: Record<AgentStatus, { dot: string; label: string }> = {
  ONLINE: { dot: 'bg-emerald-500', label: 'manager.live.status.online' },
  BUSY: { dot: 'bg-amber-500', label: 'manager.live.status.busy' },
  OFFLINE: { dot: 'bg-slate-400 dark:bg-slate-600', label: 'manager.live.status.offline' },
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('')
}

export function AgentStatusList({ agents, darkMode }: Props) {
  const { t } = useTranslation()

  // Buckets pour les compteurs en haut
  const buckets = {
    busy: agents.filter((a) => a.is_busy).length,
    online: agents.filter((a) => !a.is_busy && a.status === 'ONLINE').length,
    offline: agents.filter((a) => a.status === 'OFFLINE').length,
  }

  return (
    <div
      className={cn(
        'rounded-[10px] border shadow-sm overflow-hidden',
        darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200',
      )}
    >
      <div
        className={cn(
          'px-4 py-3 border-b',
          darkMode ? 'border-slate-700' : 'border-slate-200',
        )}
      >
        <div
          className={cn(
            'text-[13px] font-bold',
            darkMode ? 'text-white' : 'text-slate-900',
          )}
        >
          {t('manager.live.agentStatusTitle')}
        </div>
        <div
          className={cn(
            'text-xs mt-px',
            darkMode ? 'text-slate-500' : 'text-slate-400',
          )}
        >
          {t('manager.live.agentStatusSummary', {
            busy: buckets.busy,
            online: buckets.online,
            offline: buckets.offline,
          })}
        </div>
      </div>
      {agents.length === 0 ? (
        <p
          className={cn(
            'text-[13px] py-6 text-center',
            darkMode ? 'text-slate-500' : 'text-slate-400',
          )}
        >
          {t('manager.live.noAgents')}
        </p>
      ) : (
        <ul className="p-2">
          {agents.map((a) => {
            const effectiveStatus: AgentStatus = a.is_busy ? 'BUSY' : a.status
            const tone = STATUS_TONE[effectiveStatus]
            const isManager = a.role === 'MANAGER'
            const offline = effectiveStatus === 'OFFLINE'
            return (
              <li
                key={a.user_id}
                className={cn(
                  'flex items-center gap-2.5 px-2 py-2 rounded-lg',
                  darkMode ? 'hover:bg-slate-700/40' : 'hover:bg-slate-50',
                )}
              >
                <div
                  className={cn(
                    'h-[30px] w-[30px] rounded-full border flex items-center justify-center font-semibold text-[11px] flex-shrink-0',
                    darkMode
                      ? 'bg-slate-700/60 border-slate-600 text-slate-300'
                      : 'bg-slate-50 border-slate-200 text-slate-500',
                  )}
                >
                  {getInitials(a.name) || <User className="h-3 w-3" />}
                </div>
                <div className="flex-1 min-w-0">
                  <span
                    className={cn(
                      'text-[13px] font-medium truncate flex items-center gap-1.5',
                      darkMode ? 'text-white' : 'text-slate-900',
                    )}
                  >
                    {a.name}
                    {isManager && (
                      <Crown className="h-3 w-3 text-amber-500/80 flex-shrink-0" />
                    )}
                  </span>
                  <span
                    className={cn(
                      'text-xs',
                      effectiveStatus === 'BUSY'
                        ? 'text-amber-600 dark:text-amber-400 font-semibold'
                        : darkMode
                          ? 'text-slate-500'
                          : 'text-slate-400',
                    )}
                  >
                    {t(tone.label)}
                  </span>
                </div>
                <span
                  className={cn(
                    'h-2 w-2 rounded-full flex-shrink-0',
                    tone.dot,
                    offline && 'opacity-50',
                  )}
                />
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
